import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { TagModule } from 'primeng/tag';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { Dialog } from 'primeng/dialog';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { CardContent } from '@/shared/components/card-content/card-content';
import { EstadoRotulo } from '@/shared/components/layout/estado-rotulo/estado-rotulo';
import { NotificacoesService } from '@/core/services/notificacoes.service';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { PermissionService } from '@/core/permissions/permission.service';
import { UF_CFM } from '@/core/permissions/permission.model';
import { SelectEstado } from '@/shared/components/layout/select-estado/select-estado';
import { FiltrosBar } from '@/shared/components/layout/filtros-bar/filtros-bar';
import { FiltroCampo } from '@/shared/components/layout/filtros-bar/filtro-campo';
import { PerfisService } from '@/core/api/sicom-api/sistema/perfis/perfis.service';
import { PerfilResumo, PerfilUsuario } from '@/core/api/sicom-api/sistema/perfis/perfis.model';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
@Component({
    selector: 'app-perfis-catalogo',
    standalone: true,
    imports: [DialogoPadraoDirective, FormsModule, TableModule, ButtonModule, TooltipModule, TagModule, InputTextModule, SelectModule, Dialog, ConfirmDialogModule, CardContent, CanDirective, EstadoRotulo, SelectEstado, FiltrosBar, FiltroCampo],
    providers: [ConfirmationService],
    templateUrl: './perfis-catalogo.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
/** Catálogo de perfis de acesso: lista, filtra por UF/nome/status e aciona novo/editar/inativar (o editor é a Task 8). */
export class PerfisCatalogo implements OnInit {
    private readonly service = inject(PerfisService);
    private readonly perm = inject(PermissionService);
    private readonly notificacoes = inject(NotificacoesService);
    private readonly confirm = inject(ConfirmationService);
    private readonly destroyRef = inject(DestroyRef);

    /** Pede a abertura do editor: `null` para um novo perfil, ou o id do perfil a editar. */
    abrirEditor = output<number | null>();

    protected readonly perfis = signal<PerfilResumo[]>([]);

    /**
     * A ordem em que a tabela mostra os perfis: UF nacional primeiro, demais UFs em ordem
     * alfabética de sigla e, dentro de cada uma, os perfis por nome.
     *
     * <p>O `rowGroupMode="subheader"` do PrimeNG **não agrupa** — ele só desenha um cabeçalho
     * quando o valor de `groupRowsBy` muda de uma linha para a seguinte. Ordenar aqui não é
     * cosmético: com a lista fora de ordem, a mesma UF apareceria como vários grupos separados.</p>
     *
     * <p>`localeCompare` com `pt-BR` porque os nomes têm acento — em ordenação binária "Órgãos"
     * cairia depois de "Sistema".</p>
     */
    protected readonly perfisOrdenados = computed(() =>
        [...this.perfis()].sort((a, b) => {
            const ufA = (a.uf ?? '').toUpperCase();
            const ufB = (b.uf ?? '').toUpperCase();
            if (ufA !== ufB) {
                if (ufA === UF_CFM) return -1;
                if (ufB === UF_CFM) return 1;
                return ufA.localeCompare(ufB);
            }
            return (a.nmPerfil ?? '').localeCompare(b.nmPerfil ?? '', 'pt-BR');
        })
    );

    protected readonly carregando = signal(false);
    protected readonly filtroNome = signal('');
    protected readonly filtroStatus = signal<string | null>(null);
    /** UF do filtro. `BR` (ou vazio) = todas, e é o que o CFM vê ao abrir. */
    protected readonly filtroUf = signal('');
    /** Regional não escolhe UF: o backend impõe a dele, e o select reflete isso em vez de mentir. */
    protected readonly ufTravada = computed(() => !this.perm.isCfm);

    protected readonly opcoesStatus = [
        { label: 'Ativos', value: 'S' },
        { label: 'Inativos', value: 'N' }
    ];

    protected readonly rolePermissaoAdministrar = PERMISSOES.configuracoes.usuarios.permissoes.administrar;

    // ── Ver usuários do perfil (Task 13): contagem da tabela vira acionável ──
    protected readonly dialogUsuariosAberto = signal(false);
    protected readonly usuariosDoPerfilSel = signal<PerfilUsuario[]>([]);
    protected readonly carregandoUsuarios = signal(false);
    protected readonly erroUsuarios = signal(false);

    ngOnInit(): void {
        // Regional: a UF do operador já é conhecida sem ida ao servidor — o filtro nasce nela, travado.
        // CFM: nasce em `BR`, que o backend lê como "todas".
        this.filtroUf.set(this.ufOperador() || UF_CFM);
        this.carregar();
    }

    /** UF imposta ao operador regional; `''` para o CFM, que escolhe livremente. */
    private ufOperador(): string {
        return this.ufTravada() ? (this.perm.ufUsuario ?? '').trim().toUpperCase() : '';
    }

    protected carregar(): void {
        this.carregando.set(true);
        const nome = this.filtroNome().trim() || undefined;
        const inAtivo = this.filtroStatus() ?? undefined;
        // Travado, a UF vem do escopo e não do select — que apenas a exibe. Livre, vale a escolha do
        // CFM; `BR` (ou vazio) significa "todas" e vai como `undefined`, sem parâmetro na query.
        const escolhida = this.ufTravada() ? this.ufOperador() : this.filtroUf().trim().toUpperCase();
        const uf = escolhida && escolhida !== UF_CFM ? escolhida : undefined;
        this.service
            .listar(inAtivo, nome, uf)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (perfis) => {
                    this.perfis.set(perfis);
                    this.carregando.set(false);
                },
                error: () => {
                    this.notificacoes.notificar({ toast: false, severity: 'error', summary: 'Erro ao carregar perfis' });
                    this.carregando.set(false);
                }
            });
    }

    /** Reaplica UF/nome/status atuais contra o backend (filtro é server-side). */
    protected aplicarFiltros(): void {
        this.carregar();
    }

    protected limparFiltros(): void {
        this.filtroNome.set('');
        this.filtroStatus.set(null);
        // Limpar não destrava a UF do regional: ela volta para a dele, nunca para "todas".
        this.filtroUf.set(this.ufOperador() || UF_CFM);
        this.carregar();
    }

    protected novo(): void {
        this.abrirEditor.emit(null);
    }

    protected editar(perfil: PerfilResumo): void {
        this.abrirEditor.emit(perfil.id);
    }

    /** "0 usuários" / "1 usuário" / "N usuários" — singular só para exatamente 1. */
    protected formatarUsuarios(qtd: number): string {
        return `${qtd} ${qtd === 1 ? 'usuário' : 'usuários'}`;
    }

    protected confirmarInativar(perfil: PerfilResumo): void {
        this.confirm.confirm({
            header: 'Inativar perfil',
            message: `Inativar o perfil "${perfil.nmPerfil}"? Ele deixará de estar disponível para novas atribuições.`,
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Inativar',
            rejectLabel: 'Cancelar',
            accept: () => this.inativar(perfil.id)
        });
    }

    private inativar(id: number): void {
        this.service
            .inativar(id)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    this.notificacoes.notificar({ severity: 'success', summary: 'Perfil inativado.' });
                    this.carregar();
                },
                error: () => this.notificacoes.notificar({ toast: false, severity: 'error', summary: 'Erro ao inativar perfil' })
            });
    }

    protected confirmarReativar(perfil: PerfilResumo): void {
        this.confirm.confirm({
            header: 'Reativar perfil',
            message: `Reativar o perfil "${perfil.nmPerfil}"? Ele volta a ficar disponível para atribuição.`,
            icon: 'pi pi-check-circle',
            acceptLabel: 'Reativar',
            rejectLabel: 'Cancelar',
            accept: () => this.reativar(perfil.id)
        });
    }

    private reativar(id: number): void {
        this.service
            .ativar(id)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    this.notificacoes.notificar({ severity: 'success', summary: 'Perfil reativado.' });
                    this.carregar();
                },
                error: () => this.notificacoes.notificar({ toast: false, severity: 'error', summary: 'Erro ao reativar perfil' })
            });
    }

    protected confirmarExcluir(perfil: PerfilResumo): void {
        this.confirm.confirm({
            header: 'Excluir perfil',
            message: `Excluir o perfil "${perfil.nmPerfil}"? As roles já atribuídas aos usuários serão mantidas; o perfil deixa de ser aplicável.`,
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Excluir',
            rejectLabel: 'Cancelar',
            acceptButtonStyleClass: 'p-button-danger',
            accept: () => this.excluir(perfil.id)
        });
    }

    private excluir(id: number): void {
        this.service
            .excluir(id)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    this.notificacoes.notificar({ severity: 'success', summary: 'Perfil excluído.' });
                    this.carregar();
                },
                error: () => this.notificacoes.notificar({ toast: false, severity: 'error', summary: 'Erro ao excluir perfil' })
            });
    }

    /** Chamado pelo editor (Task 8) após salvar, para a lista refletir a mudança. */
    recarregar(): void {
        this.carregar();
    }

    /**
     * Abre o diálogo "Usuários com este perfil". Com `qtdUsuarios === 0` não há o que buscar — a
     * ação nem fica disponível na tabela (ver template), e este guard cobre qualquer chamada
     * direta; não abrimos o diálogo com estado vazio nesse caso, pra não gastar uma chamada de
     * rede e um clique a mais por algo que a própria contagem "0" já diz.
     */
    protected abrirUsuarios(perfil: PerfilResumo): void {
        if (perfil.qtdUsuarios === 0) return;
        this.dialogUsuariosAberto.set(true);
        this.carregandoUsuarios.set(true);
        this.erroUsuarios.set(false);
        this.usuariosDoPerfilSel.set([]);
        this.service
            .usuariosDoPerfil(perfil.id)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (usuarios) => {
                    this.usuariosDoPerfilSel.set(usuarios);
                    this.carregandoUsuarios.set(false);
                },
                error: () => {
                    this.erroUsuarios.set(true);
                    this.carregandoUsuarios.set(false);
                }
            });
    }

    protected fecharUsuarios(): void {
        this.dialogUsuariosAberto.set(false);
        this.usuariosDoPerfilSel.set([]);
        this.erroUsuarios.set(false);
    }
}
